import * as React from 'react';
import { translations, Language } from './translations';

type TranslationType = typeof translations[Language];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationType;
}

const LanguageContext = React.createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'schemagen-language';

function getInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'en';

  // Detect from URL path
  const path = window.location.pathname;
  if (path.startsWith('/fr/') || path === '/fr') return 'fr';
  if (path.startsWith('/es/') || path === '/es') return 'es';

  // For root paths, check localStorage for returning users
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ['fr', 'en', 'es'].includes(stored)) {
      return stored as Language;
    }
  } catch {
    // localStorage might not be available
  }

  // Default to English (root = English)
  return 'en';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = React.useState<Language>(getInitialLanguage);

  const setLanguage = React.useCallback((lang: Language) => {
    setLanguageState(prev => {
      if (prev === lang) return prev;
      return lang;
    });

    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // localStorage might not be available
    }
  }, []);

  React.useEffect(() => {
    // Set initial document lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const value = React.useMemo(() => ({
    language,
    setLanguage,
    t: translations[language],
  }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = React.useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
